import {inject, Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { PartialSale, TradeEntry } from '../models/trade-entry';
import { RiskProfile, RiskStep } from '../models/risk-setup';
import { AiPrompt, AiPromptTemplate, ChatGptRunResult } from '../models/ai-prompt';
import { Field, FieldText } from '../models/field';
import {AppConfigService} from "@app/app-config.service";
import { DailyJournalEntry } from '../models/daily-journal-entry';
import { AppSettings } from '../models/app-settings';

export interface SetupPerformance {
  setup: string;
  totalResults: number;
  positiveResults: number;
  negativeResults: number;
  neutralResults: number;
  successRatePct: number;
  totalPnl: number;
}

export interface SetupPerformanceResponse {
  success: boolean;
  errorCode?: string | null;
  message?: string | null;
  items: SetupPerformance[];
}

export interface TradeAnalysisRequest {
  fileName?: string;
  rawJson: string;
}

export interface TradeAnalysisItem {
  id: string;
  fileName?: string | null;
  symbol?: string | null;
  instrument?: string | null;
  analysisDate?: string | null;
  analysisDateTime?: string | null;
  biasDirection?: string | null;
  biasStrength?: number | null;
  variantCount?: number | null;
  appliedTradeId?: string | null;
  rawJson: string;
  createdAt: string;
}

export interface TradeAnalysisResponse {
  success: boolean;
  errorCode?: string | null;
  message?: string | null;
  item?: TradeAnalysisItem | null;
  items?: TradeAnalysisItem[] | null;
}

export interface ChartAnalysisRequest {
  fileName?: string;
  imageDataUrl: string;
  agentPrompt?: string;
}

export interface ChartAnalysisResponse {
  success: boolean;
  errorCode?: string | null;
  message?: string | null;
  rawJson?: string | null;
  item?: TradeAnalysisItem | null;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  //private readonly baseUrl = environment.apiBaseUrl.replace(/\/$/, '');
  private cfg = inject(AppConfigService);
  private tradeChangesSubject = new Subject<void>();

  readonly tradeChanges$ = this.tradeChangesSubject.asObservable();



  constructor(private http: HttpClient) {}

  private get baseUrl(): string {
    return this.cfg.apiBaseUrl.replace(/\/$/, '');
  }

  // Trades
  getTrades(feldTextId?: string): Observable<TradeEntry[]> {
    const params = feldTextId ? { params: { feld_text_id: feldTextId } } : {};
    return this.http.get<TradeEntry[]>(`${this.baseUrl}/api/trades`, params);
  }

  createTrade(entry: TradeEntry): Observable<TradeEntry> {
    return this.http.post<TradeEntry>(`${this.baseUrl}/api/trades`, entry)
      .pipe(tap(() => this.tradeChangesSubject.next()));
  }

  updateTrade(id: string, entry: TradeEntry): Observable<TradeEntry> {
    return this.http.put<TradeEntry>(`${this.baseUrl}/api/trades/${id}`, entry)
      .pipe(tap(() => this.tradeChangesSubject.next()));
  }

  deleteTrade(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/trades/${id}`)
      .pipe(tap(() => this.tradeChangesSubject.next()));
  }

  deleteAllTrades(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/trades`)
      .pipe(tap(() => this.tradeChangesSubject.next()));
  }

  updateTradeEntry(id: string, entry: TradeEntry): Observable<TradeEntry> {
    return this.http.put<TradeEntry>(`${this.baseUrl}/api/trades/${id}/entry`, entry)
      .pipe(tap(() => this.tradeChangesSubject.next()));
  }

  screenshotImageUrl(id: string): string {
    return `${this.baseUrl}/api/trades/screenshots/${encodeURIComponent(id)}/image`;
  }

  getSetupPerformance(): Observable<SetupPerformanceResponse> {
    return this.http.get<SetupPerformanceResponse>(`${this.baseUrl}/api/statistics/setup-performance`);
  }

  createTradeAnalysis(request: TradeAnalysisRequest): Observable<TradeAnalysisResponse> {
    return this.http.post<TradeAnalysisResponse>(`${this.baseUrl}/api/trade-analyses`, request);
  }

  getTradeAnalyses(): Observable<TradeAnalysisResponse> {
    return this.http.get<TradeAnalysisResponse>(`${this.baseUrl}/api/trade-analyses`);
  }

  runChartAnalysis(request: ChartAnalysisRequest): Observable<ChartAnalysisResponse> {
    return this.http.post<ChartAnalysisResponse>(`${this.baseUrl}/api/chart-analyses/run`, request);
  }

  // Risk profile
  getRiskProfile(planYear?: number): Observable<RiskProfile> {
    const params = planYear ? { params: { id: planYear.toString() } } : {};
    return this.http.get<RiskProfile>(`${this.baseUrl}/api/risk-profile`, params);
  }

  getRiskProfileIds(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/api/risk-profile/ids`);
  }

  saveRiskProfile(profile: RiskProfile): Observable<RiskProfile> {
    return this.http.post<RiskProfile>(`${this.baseUrl}/api/risk-profile`, profile);
  }

  updateRiskProfile(profile: RiskProfile): Observable<RiskProfile> {
    return this.http.put<RiskProfile>(`${this.baseUrl}/api/risk-profile`, profile);
  }

  deleteRiskProfile(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/risk-profile`);
  }

  // Risk steps
  getRiskSteps(profileId: string): Observable<RiskStep[]> {
    return this.http.get<RiskStep[]>(`${this.baseUrl}/api/risk-steps`, { params: { id: profileId } });
  }

  createRiskStep(step: RiskStep, profileId: string): Observable<RiskStep> {
    return this.http.post<RiskStep>(`${this.baseUrl}/api/risk-steps`, step, { params: { id: profileId } });
  }

  updateRiskStep(id: number, step: RiskStep): Observable<RiskStep> {
    return this.http.put<RiskStep>(`${this.baseUrl}/api/risk-steps/${id}`, step);
  }

  deleteRiskStep(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/risk-steps/${id}`);
  }

  createPartialSale(tradeId: string, partialSale: PartialSale): Observable<PartialSale> {
    return this.http.post<PartialSale>(`${this.baseUrl}/api/partial-sales`, partialSale, { params: { tradeId } })
      .pipe(tap(() => this.tradeChangesSubject.next()));
  }

  // AI prompts
  getAiPrompt(): Observable<AiPrompt> {
    return this.http.get<AiPrompt>(`${this.baseUrl}/api/ai/prompt`);
  }

  saveAiPrompt(prompt: AiPrompt): Observable<AiPrompt> {
    return this.http.post<AiPrompt>(`${this.baseUrl}/api/ai/prompt`, prompt);
  }

  deleteAiPrompt(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/ai/prompt`);
  }

  getAiPromptTemplates(): Observable<AiPromptTemplate[]> {
    return this.http.get<AiPromptTemplate[]>(`${this.baseUrl}/api/ai/prompts`);
  }

  createAiPromptTemplate(template: AiPromptTemplate): Observable<AiPromptTemplate> {
    return this.http.post<AiPromptTemplate>(`${this.baseUrl}/api/ai/prompts`, template);
  }

  updateAiPromptTemplate(id: string, template: AiPromptTemplate): Observable<AiPromptTemplate> {
    return this.http.put<AiPromptTemplate>(`${this.baseUrl}/api/ai/prompts/${id}`, template);
  }

  deleteAiPromptTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/ai/prompts/${id}`);
  }

  deleteAllAiPromptTemplates(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/ai/prompts`);
  }

  runPrompt(text: string, systemPrompt: string): Observable<ChatGptRunResult> {
    return this.http.post<ChatGptRunResult>(`${this.baseUrl}/api/ai/run/prompt`, {
      text,
      systemPrompt,
    });
  }


  // Fields
  getFields(): Observable<Field[]> {
    return this.http.get<Field[]>(`${this.baseUrl}/api/fields`);
  }

  createField(field: Field): Observable<Field> {
    return this.http.post<Field>(`${this.baseUrl}/api/fields`, field);
  }

  updateField(feldId: string, field: Field): Observable<Field> {
    return this.http.put<Field>(`${this.baseUrl}/api/fields/${feldId}`, field);
  }

  deleteField(feldId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/fields/${feldId}`);
  }

  getFieldTexts(feldId: string): Observable<FieldText[]> {
    return this.http.get<FieldText[]>(`${this.baseUrl}/api/fields/${feldId}/texts`);
  }

  createFieldText(feldId: string, fieldText: FieldText): Observable<FieldText> {
    return this.http.post<FieldText>(`${this.baseUrl}/api/fields/${feldId}/texts`, fieldText);
  }

  updateFieldText(feldId: string, feldTextId: string, fieldText: FieldText): Observable<FieldText> {
    return this.http.put<FieldText>(`${this.baseUrl}/api/fields/${feldId}/texts/${feldTextId}`, fieldText);
  }

  deleteFieldText(feldId: string, feldTextId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/fields/${feldId}/texts/${feldTextId}`);
  }

  getDailyJournalEntries(): Observable<DailyJournalEntry[]> {
    return this.http.get<DailyJournalEntry[]>(`${this.baseUrl}/api/daily-journal`);
  }

  getAppSettings(): Observable<AppSettings> {
    return this.http.get<AppSettings>(`${this.baseUrl}/api/app-settings`);
  }

  updateAppSettings(settings: AppSettings): Observable<AppSettings> {
    return this.http.put<AppSettings>(`${this.baseUrl}/api/app-settings`, settings);
  }

  createDailyJournalEntry(entry: DailyJournalEntry): Observable<DailyJournalEntry> {
    return this.http.post<DailyJournalEntry>(`${this.baseUrl}/api/daily-journal`, entry);
  }

  updateDailyJournalEntry(id: string, entry: DailyJournalEntry): Observable<DailyJournalEntry> {
    return this.http.put<DailyJournalEntry>(`${this.baseUrl}/api/daily-journal/${id}`, entry);
  }

  deleteDailyJournalEntry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/daily-journal/${id}`);
  }

  getExchangeRate(currency: string): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}/api/currency-rates`, { params: { currency } });
  }
}
